import { Fragment, type ReactNode } from 'react'

import type { RichTextFlow, RichTextPhrasing, TextValue } from '@/lib/content-standard/rich-text'
import { Link } from '@/i18n/navigation'

export interface ExternalLinkProps {
    readonly href: string
    readonly title?: string
    readonly children: ReactNode
}

/** How a link off the site renders: a plain anchor, unless the caller wraps it in what its
 *  surface needs around an outbound link, such as its click tracking. */
export type ExternalLinkRenderer = (props: ExternalLinkProps) => ReactNode

const plainAnchor: ExternalLinkRenderer = ({ href, title, children }) => (
    <a href={href} title={title}>
        {children}
    </a>
)

/**
 * A rich slot's text value as elements this renderer owns. Content chooses only among the
 * rich-text node kinds and supplies no attributes beyond a link's target and title. A link
 * to one of our pages renders through the i18n `Link`, which leaves the href's locale
 * prefix as written, and no link carries `nofollow`
 * (→ invariants.md#internal-links-pass-equity).
 */
export function RichText({
    value,
    externalLink = plainAnchor,
}: {
    readonly value: TextValue
    readonly externalLink?: ExternalLinkRenderer
}) {
    if (!value.ast) {
        return value.text === '' ? null : <p>{value.text}</p>
    }

    /** In a tight list an item's paragraphs render as their content alone. */
    const flow = (node: RichTextFlow, key: number, tight: boolean): ReactNode => {
        if (node.type === 'paragraph') {
            const content = node.children.map(phrasing)
            return tight ? content : <p key={key}>{content}</p>
        }
        const items = node.children.map((item, index) => (
            <li key={index}>
                {item.children.map((child, childIndex) =>
                    flow(child, childIndex, !node.spread && !item.spread),
                )}
            </li>
        ))
        return node.ordered ? (
            <ol key={key} start={node.start ?? undefined}>
                {items}
            </ol>
        ) : (
            <ul key={key}>{items}</ul>
        )
    }

    const phrasing = (node: RichTextPhrasing, key: number): ReactNode => {
        switch (node.type) {
            case 'text':
                return node.value
            case 'emphasis':
                return <em key={key}>{node.children.map(phrasing)}</em>
            case 'strong':
                return <strong key={key}>{node.children.map(phrasing)}</strong>
            case 'link': {
                const title = node.title ?? undefined
                const children = node.children.map(phrasing)
                return node.url.startsWith('/') ? (
                    <Link key={key} href={node.url} title={title}>
                        {children}
                    </Link>
                ) : (
                    <Fragment key={key}>
                        {externalLink({ href: node.url, title, children })}
                    </Fragment>
                )
            }
        }
    }

    return <>{value.ast.children.map((node, index) => flow(node, index, false))}</>
}
